<template>
  <div class="crm-detail-page">
    <div class="page-header">
      <b-button variant="flat-secondary" size="sm" @click="$router.push({ name: 'crm-clientes' })">
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
        Voltar
      </b-button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-1 text-muted">Carregando dados do cliente...</p>
    </div>

    <div v-else-if="cliente" class="detail-layout">
      <aside class="detail-sidebar">
        <b-card class="sidebar-card" no-body>
          <div class="client-summary">
            <h4 class="client-name">{{ cliente.clienterazaosocial }}</h4>
            <p class="client-fantasia text-muted" v-if="cliente.clientenomefantasia">{{ cliente.clientenomefantasia }}</p>
            <p class="client-cnpj text-muted">{{ formatCnpj(cliente.clientecnpj || cliente.clientecpf) }}</p>
            <div class="client-badges">
              <b-badge :variant="getTipoBadgeVariant(cliente.crm_tipo)" pill>
                {{ cliente.crm_tipo || 'N/A' }}
              </b-badge>
              <b-badge :variant="cliente.clienteinativo ? 'light-danger' : 'light-success'" pill>
                {{ cliente.clienteinativo ? 'Inativo' : 'Ativo' }}
              </b-badge>
            </div>
          </div>
        </b-card>

        <b-card class="sidebar-card" no-body>
          <div class="stats-section">
            <h6 class="stats-title text-muted">Resumo</h6>
            <div class="stat-row" @click="activeTab = 3">
              <feather-icon icon="CalendarIcon" size="15" class="text-primary" />
              <span class="stat-label">Atividades</span>
              <span class="stat-count">{{ stats.atividades || 0 }}</span>
            </div>
            <div class="stat-row" @click="activeTab = 3">
              <feather-icon icon="TargetIcon" size="15" class="text-warning" />
              <span class="stat-label">Oportunidades</span>
              <span class="stat-count">{{ stats.oportunidades || 0 }}</span>
            </div>
            <div class="stat-row" @click="activeTab = 4">
              <feather-icon icon="ShoppingBagIcon" size="15" class="text-success" />
              <span class="stat-label">Pedidos</span>
              <span class="stat-count">{{ stats.pedidos || 0 }}</span>
            </div>
            <div class="stat-row">
              <feather-icon icon="FileTextIcon" size="15" class="text-info" />
              <span class="stat-label">PICs</span>
              <span class="stat-count">{{ stats.pics || 0 }}</span>
            </div>
            <div class="stat-row" @click="activeTab = 2">
              <feather-icon icon="UsersIcon" size="15" class="text-secondary" />
              <span class="stat-label">Contatos</span>
              <span class="stat-count">{{ stats.contatos || 0 }}</span>
            </div>
            <div class="stat-row" @click="activeTab = 0">
              <feather-icon icon="MessageSquareIcon" size="15" class="text-dark" />
              <span class="stat-label">Notas</span>
              <span class="stat-count">{{ stats.notas || 0 }}</span>
            </div>
          </div>
        </b-card>

        <b-card class="sidebar-card" no-body>
          <div class="activities-section">
            <h6 class="stats-title text-muted">Próximas Atividades</h6>
            <div v-if="proximasAtividades.length === 0" class="no-items text-muted">Nenhuma atividade pendente</div>
            <div v-for="atv in proximasAtividades" :key="atv.id" class="activity-item">
              <div class="activity-info">
                <span class="activity-title">{{ atv.titulo }}</span>
                <span class="activity-date text-muted">{{ formatDateTime(atv.data_inicio) }}</span>
              </div>
              <b-button
                variant="flat-success"
                size="sm"
                @click="onConcluirAtividade(atv)"
                title="Concluir"
              >
                <feather-icon icon="CheckIcon" size="14" />
              </b-button>
            </div>
          </div>
        </b-card>
      </aside>

      <main class="detail-main">
        <b-card no-body class="main-card">
          <b-tabs v-model="activeTab" card nav-class="crm-tabs" @input="onTabChange">
            <b-tab title="Timeline">
              <div class="tab-content-inner">
                <div class="timeline-filters">
                  <b-form-select v-model="notaFilter" size="sm" class="w-auto" @change="loadNotas">
                    <b-form-select-option value="todas">Todas</b-form-select-option>
                    <b-form-select-option value="MANUAL">Manuais</b-form-select-option>
                    <b-form-select-option value="AUTOMATICA">Automáticas</b-form-select-option>
                  </b-form-select>
                </div>

                <div
                  class="add-note-form"
                  :class="{ 'drag-over': isDragOver }"
                  @dragover.prevent="isDragOver = true"
                  @dragleave.prevent="isDragOver = false"
                  @drop.prevent="onDropFile"
                >
                  <b-form-textarea
                    v-model="newNota"
                    placeholder="Adicionar uma nota... (cole imagens com Ctrl+V ou arraste arquivos)"
                    rows="3"
                    class="note-input"
                    @paste="onPasteFile"
                  />
                  <div v-if="notaAnexo" class="anexo-preview bg-light border">
                    <div class="anexo-info">
                      <feather-icon :icon="getFileIcon(notaAnexo.type)" size="16" class="mr-50" />
                      <span class="anexo-name">{{ notaAnexo.name }}</span>
                      <span class="anexo-size text-muted">({{ formatFileSize(notaAnexo.size) }})</span>
                    </div>
                    <img v-if="notaAnexoPreview && notaAnexo.type.startsWith('image/')" :src="notaAnexoPreview" class="anexo-thumb" />
                    <b-button variant="flat-danger" size="sm" @click="removeAnexo" class="ml-50">
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </div>
                  <div class="note-actions">
                    <div class="note-actions-left">
                      <input ref="fileInput" type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" class="d-none" @change="onFileSelected" />
                      <b-button variant="flat-secondary" size="sm" @click="$refs.fileInput.click()" title="Anexar arquivo">
                        <feather-icon icon="PaperclipIcon" size="14" />
                      </b-button>
                    </div>
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="(!newNota.trim() && !notaAnexo) || savingNota"
                      @click="addNota"
                    >
                      <b-spinner v-if="savingNota" small class="mr-50" />
                      Adicionar Nota
                    </b-button>
                  </div>
                </div>

                <div class="timeline-list">
                  <div v-if="loadingNotas" class="text-center py-3">
                    <b-spinner small variant="primary" />
                  </div>
                  <div v-else-if="notas.length === 0" class="no-items py-3 text-muted">Nenhuma nota encontrada</div>
                  <div v-else>
                    <div v-for="nota in notas" :key="nota.id" class="note-item">
                      <div class="note-header">
                        <b-badge :variant="isManualNota(nota) ? 'light-primary' : 'light-secondary'" pill>
                          {{ isManualNota(nota) ? 'Manual' : 'Automática' }}
                        </b-badge>
                        <span class="note-date text-muted">{{ formatDateTime(nota.created_at) }}</span>
                      </div>
                      <div class="note-author text-muted" v-if="nota.autor_nome">
                        <feather-icon icon="UserIcon" size="12" class="mr-25" />
                        {{ nota.autor_nome }}
                      </div>
                      <p class="note-text" v-if="nota.conteudo_texto">{{ nota.conteudo_texto }}</p>
                      <div v-if="nota.anexo_path" class="note-attachment">
                        <img
                          v-if="isImageMime(nota.anexo_tipo)"
                          :src="getAnexoUrl(nota.anexo_path)"
                          class="attachment-image border"
                          @click="openAnexo(nota)"
                        />
                        <a
                          v-else
                          :href="getAnexoUrl(nota.anexo_path)"
                          target="_blank"
                          class="attachment-file bg-light text-primary"
                        >
                          <feather-icon :icon="getFileIcon(nota.anexo_tipo)" size="16" class="mr-50" />
                          <span>{{ nota.anexo_nome }}</span>
                          <span class="attachment-size text-muted" v-if="nota.anexo_tamanho">({{ formatFileSize(nota.anexo_tamanho) }})</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>

            <b-tab title="Dados">
              <div class="tab-content-inner">

                <div class="dados-section">
                  <h6 class="dados-section-title">
                    <feather-icon icon="InfoIcon" size="15" class="mr-50" />
                    Dados Gerais
                  </h6>
                  <b-row>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Razão Social</span>
                        <span class="dados-value">{{ cliente.clienterazaosocial || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Nome Fantasia</span>
                        <span class="dados-value">{{ cliente.clientenomefantasia || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Nome Abreviado</span>
                        <span class="dados-value">{{ cliente.clientenomeabreviado || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Tipo Pessoa</span>
                        <span class="dados-value">{{ cliente.clientetipopessoa || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">{{ cliente.clientetipopessoa === 'FISICA' ? 'CPF' : 'CNPJ' }}</span>
                        <span class="dados-value">{{ formatCnpj(cliente.clientecnpj || cliente.clientecpf) || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4" v-if="cliente.clientetipopessoa === 'FISICA'">
                      <div class="dados-field">
                        <span class="dados-label">RG</span>
                        <span class="dados-value">{{ cliente.clienterg || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Inscrição Estadual</span>
                        <span class="dados-value">{{ cliente.clienteisentoie ? 'Isento' : (cliente.clienteie || '-') }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Inscrição Municipal</span>
                        <span class="dados-value">{{ cliente.clienteinscricaomunicipal || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Data Cadastro</span>
                        <span class="dados-value">{{ formatDate(cliente.clientedatacadastro) || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Status</span>
                        <span class="dados-value">
                          <b-badge :variant="cliente.clienteinativo ? 'light-danger' : 'light-success'" pill>
                            {{ cliente.clienteinativo ? 'Inativo' : 'Ativo' }}
                          </b-badge>
                        </span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Código ERP</span>
                        <span class="dados-value">{{ cliente.clienteiderp || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Representante</span>
                        <span class="dados-value">{{ cliente.representantenome || '-' }}</span>
                      </div>
                    </b-col>
                  </b-row>
                </div>

                <div class="dados-section">
                  <h6 class="dados-section-title">
                    <feather-icon icon="PhoneIcon" size="15" class="mr-50" />
                    Contato / E-mails
                  </h6>
                  <b-row>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Telefone 1</span>
                        <span class="dados-value">{{ cliente.clientetelefonecontato1 || '-' }}{{ cliente.clienteramal1 ? ' (Ramal: ' + cliente.clienteramal1 + ')' : '' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Telefone 2</span>
                        <span class="dados-value">{{ cliente.clientetelefonecontato2 || '-' }}{{ cliente.clienteramal2 ? ' (Ramal: ' + cliente.clienteramal2 + ')' : '' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">E-mail Comercial</span>
                        <span class="dados-value">{{ cliente.clienteemailcomercial || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">E-mail NF-e</span>
                        <span class="dados-value">{{ cliente.clienteemailnfe || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">E-mail Secundário</span>
                        <span class="dados-value">{{ cliente.clienteemail2 || '-' }}</span>
                      </div>
                    </b-col>
                  </b-row>
                </div>

                <div class="dados-section">
                  <h6 class="dados-section-title">
                    <feather-icon icon="FileTextIcon" size="15" class="mr-50" />
                    Dados Fiscais / Comerciais
                  </h6>
                  <b-row>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">CNAE</span>
                        <span class="dados-value">{{ cliente.clientecnae || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Contribuinte ICMS</span>
                        <span class="dados-value">{{ cliente.clientecontribuinteicms ? 'Sim' : 'Não' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Simples Nacional</span>
                        <span class="dados-value">{{ cliente.clientesimplesnacional ? 'Sim' : 'Não' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Regime Especial</span>
                        <span class="dados-value">{{ cliente.clienteregimeespecial || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Suspensão IPI</span>
                        <span class="dados-value">{{ cliente.clientesuspensaoipi ? 'Sim' : 'Não' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">SUFRAMA</span>
                        <span class="dados-value">{{ cliente.clientecodigosuframa || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Tipo Crédito</span>
                        <span class="dados-value">{{ cliente.clientetipocredito || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Limite de Crédito</span>
                        <span class="dados-value">{{ cliente.clientelimitecredito ? formatCurrency(cliente.clientelimitecredito) : '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Valor Crédito</span>
                        <span class="dados-value">{{ cliente.clientevalorcredito ? formatCurrency(cliente.clientevalorcredito) : '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Tabela de Preço</span>
                        <span class="dados-value">{{ cliente.tabelaprecoid || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Canal de Venda</span>
                        <span class="dados-value">{{ cliente.canalvendaid || '-' }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" lg="4">
                      <div class="dados-field">
                        <span class="dados-label">Faturamento Parcial</span>
                        <span class="dados-value">{{ cliente.clientefaturamentoparcial ? 'Sim' : 'Não' }}</span>
                      </div>
                    </b-col>
                  </b-row>
                  <b-row v-if="cliente.clienteobservacao || cliente.clienteobservacaofiscal || cliente.clienteobservacaofinanceiro">
                    <b-col md="12" v-if="cliente.clienteobservacao">
                      <div class="dados-field">
                        <span class="dados-label">Observação Geral</span>
                        <span class="dados-value dados-value-block">{{ cliente.clienteobservacao }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" v-if="cliente.clienteobservacaofiscal">
                      <div class="dados-field">
                        <span class="dados-label">Observação Fiscal</span>
                        <span class="dados-value dados-value-block">{{ cliente.clienteobservacaofiscal }}</span>
                      </div>
                    </b-col>
                    <b-col md="6" v-if="cliente.clienteobservacaofinanceiro">
                      <div class="dados-field">
                        <span class="dados-label">Observação Financeiro</span>
                        <span class="dados-value dados-value-block">{{ cliente.clienteobservacaofinanceiro }}</span>
                      </div>
                    </b-col>
                  </b-row>
                </div>

                <div class="dados-section" v-if="enderecos.length > 0">
                  <h6 class="dados-section-title">
                    <feather-icon icon="MapPinIcon" size="15" class="mr-50" />
                    Endereços
                  </h6>
                  <div v-for="(end, idx) in enderecos" :key="idx" class="endereco-card bg-light border">
                    <div class="endereco-header">
                      <b-badge variant="light-primary" pill>{{ end.tipoenderecodescricao || end.tipoenderecoid }}</b-badge>
                    </div>
                    <b-row>
                      <b-col md="6" lg="4">
                        <div class="dados-field">
                          <span class="dados-label">Logradouro</span>
                          <span class="dados-value">{{ end.clienteendereco || '-' }}{{ end.clienteendereconumero ? ', ' + end.clienteendereconumero : '' }}</span>
                        </div>
                      </b-col>
                      <b-col md="6" lg="4">
                        <div class="dados-field">
                          <span class="dados-label">Complemento</span>
                          <span class="dados-value">{{ end.clienteenderecocomplemento || '-' }}</span>
                        </div>
                      </b-col>
                      <b-col md="6" lg="4">
                        <div class="dados-field">
                          <span class="dados-label">Bairro</span>
                          <span class="dados-value">{{ end.clienteenderecobairro || '-' }}</span>
                        </div>
                      </b-col>
                      <b-col md="6" lg="4">
                        <div class="dados-field">
                          <span class="dados-label">Cidade / UF</span>
                          <span class="dados-value">{{ end.clienteenderecocidade || '-' }}{{ end.ufid ? ' / ' + end.ufid : '' }}</span>
                        </div>
                      </b-col>
                      <b-col md="6" lg="4">
                        <div class="dados-field">
                          <span class="dados-label">CEP</span>
                          <span class="dados-value">{{ end.clienteenderecocep || '-' }}</span>
                        </div>
                      </b-col>
                      <b-col md="6" lg="4" v-if="end.clienteenderecoie">
                        <div class="dados-field">
                          <span class="dados-label">IE Endereço</span>
                          <span class="dados-value">{{ end.clienteenderecoie }}</span>
                        </div>
                      </b-col>
                    </b-row>
                    <div v-if="end.clienteenderecoobservacoes" class="endereco-obs">
                      <span class="dados-label">Obs:</span> {{ end.clienteenderecoobservacoes }}
                    </div>
                  </div>
                </div>

                <div class="dados-section dados-section-crm bg-light border">
                  <h6 class="dados-section-title">
                    <feather-icon icon="BriefcaseIcon" size="15" class="mr-50" />
                    Dados CRM
                  </h6>
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="Tipo CRM">
                        <b-form-select v-model="editData.crm_tipo">
                          <b-form-select-option value="CLIENTE">Cliente</b-form-select-option>
                          <b-form-select-option value="LEAD">Lead</b-form-select-option>
                          <b-form-select-option value="PROSPECT">Prospect</b-form-select-option>
                          <b-form-select-option value="DESQUALIFICADO">Desqualificado</b-form-select-option>
                        </b-form-select>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group label="Observações CRM">
                    <b-form-textarea v-model="editData.crm_observacoes" rows="4" />
                  </b-form-group>
                  <b-button variant="primary" :disabled="savingDados" @click="saveDados">
                    <b-spinner v-if="savingDados" small class="mr-50" />
                    Salvar
                  </b-button>
                </div>

              </div>
            </b-tab>

            <b-tab title="Contatos">
              <div class="tab-content-inner">
                <div class="tab-toolbar">
                  <b-button variant="primary" size="sm" @click="openContatoModal()">
                    <feather-icon icon="PlusIcon" size="14" class="mr-25" /> Novo Contato
                  </b-button>
                </div>

                <b-table
                  :items="contatos"
                  :fields="contatoFields"
                  :busy="loadingContatos"
                  responsive
                  show-empty
                  empty-text="Nenhum contato encontrado"
                  class="crm-inner-table"
                >
                  <template #table-busy>
                    <div class="text-center py-3"><b-spinner small variant="primary" /></div>
                  </template>
                  <template #cell(clientecontatonome)="{ item }">
                    <div>
                      <span class="font-weight-bold">{{ item.clientecontatonome || '-' }}</span>
                      <b-badge v-if="item.clientecontatoprincipal" variant="light-success" pill class="ml-50" style="font-size:0.7rem">Principal</b-badge>
                    </div>
                    <small v-if="item.clientearea" class="text-muted">{{ item.clientearea }}</small>
                  </template>
                  <template #cell(clientecontatotelefone)="{ item }">
                    <span v-if="item.clientecontatotelefone">
                      <feather-icon icon="PhoneIcon" size="12" class="mr-25" />{{ item.clientecontatotelefone }}{{ item.clientecontatoramal ? ' R.' + item.clientecontatoramal : '' }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template #cell(clientecontatoemail)="{ item }">
                    <span v-if="item.clientecontatoemail">
                      <feather-icon icon="MailIcon" size="12" class="mr-25" />{{ item.clientecontatoemail }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template #cell(clientecontatocelular)="{ item }">
                    <span v-if="item.clientecontatocelular">
                      <feather-icon icon="SmartphoneIcon" size="12" class="mr-25" />{{ item.clientecontatocelular }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template #cell(aniversario)="{ item }">
                    {{ item.aniversario_dia && item.aniversario_mes ? (String(item.aniversario_dia).padStart(2,'0') + '/' + String(item.aniversario_mes).padStart(2,'0')) : '-' }}
                  </template>
                  <template #cell(actions)="{ item }">
                    <b-button variant="flat-primary" size="sm" @click="openContatoModal(item)" class="mr-25">
                      <feather-icon icon="EditIcon" size="14" />
                    </b-button>
                    <b-button variant="flat-danger" size="sm" @click="deleteContato(item)">
                      <feather-icon icon="TrashIcon" size="14" />
                    </b-button>
                  </template>
                </b-table>
              </div>
            </b-tab>

            <b-tab title="Oportunidades">
              <div class="tab-content-inner">
                <div class="tab-toolbar">
                  <b-button variant="primary" size="sm" @click="openOportunidadeModal()">
                    <feather-icon icon="PlusIcon" size="14" class="mr-25" /> Nova Oportunidade
                  </b-button>
                </div>

                <b-table
                  :items="oportunidades"
                  :fields="oportunidadeFields"
                  :busy="loadingOportunidades"
                  responsive
                  show-empty
                  empty-text="Nenhuma oportunidade encontrada"
                  class="crm-inner-table"
                >
                  <template #table-busy>
                    <div class="text-center py-3"><b-spinner small variant="primary" /></div>
                  </template>
                  <template #cell(status)="{ item }">
                    <b-badge :variant="getOportStatusVariant(item.status)" pill>{{ item.status }}</b-badge>
                  </template>
                  <template #cell(valor_estimado)="{ item }">
                    {{ formatCurrency(item.valor_estimado) }}
                  </template>
                  <template #cell(probabilidade)="{ item }">
                    {{ item.probabilidade != null ? item.probabilidade + '%' : '-' }}
                  </template>
                  <template #cell(data_prevista_fechamento)="{ item }">
                    {{ item.data_prevista_fechamento ? formatDate(item.data_prevista_fechamento) : '-' }}
                  </template>
                </b-table>
              </div>
            </b-tab>

            <b-tab title="Pedidos">
              <div class="tab-content-inner">
                <div v-if="loadingPedidos" class="text-center py-3">
                  <b-spinner small variant="primary" />
                </div>
                <div v-else-if="pedidos.length === 0" class="no-items py-3 text-muted">Nenhum pedido encontrado</div>
                <div v-else>
                  <div class="pedidos-resumo mb-1 bg-light">
                    <span class="pedidos-resumo-item">
                      <strong>{{ pedidosResumo.totalPedidos || 0 }}</strong> pedido(s)
                    </span>
                    <span class="pedidos-resumo-item">
                      Total: <strong>{{ formatCurrency(pedidosResumo.valorTotal) }}</strong>
                    </span>
                    <span class="pedidos-resumo-item">
                      Ticket Médio: <strong>{{ formatCurrency(pedidosResumo.ticketMedio) }}</strong>
                    </span>
                  </div>
                  <b-table
                    :items="pedidos"
                    :fields="pedidoFields"
                    responsive
                    hover
                    class="crm-inner-table"
                    @row-clicked="onPedidoClick"
                  >
                    <template #cell(pedidodata)="{ item }">
                      {{ formatDate(item.pedidodata) }}
                    </template>
                    <template #cell(pedidovalortotal)="{ item }">
                      {{ formatCurrency(item.pedidovalortotal) }}
                    </template>
                    <template #cell(pedidostatusworkflow)="{ item }">
                      <b-badge :variant="getPedidoStatusVariant(item.pedidostatusworkflow)" pill>
                        {{ item.pedidostatusworkflow || '-' }}
                      </b-badge>
                    </template>
                    <template #cell(pedidositsales)="{ item }">
                      <b-badge :variant="getSitSalesVariant(item.pedidositsales)" pill>
                        {{ item.pedidositsales || '-' }}
                      </b-badge>
                    </template>
                    <template #cell(acoes)="{ item }">
                      <b-button variant="flat-primary" size="sm" @click.stop="onPedidoClick(item)" title="Ver pedido">
                        <feather-icon icon="ExternalLinkIcon" size="14" />
                      </b-button>
                    </template>
                  </b-table>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </main>
    </div>

    <b-modal
      v-model="contatoModal.show"
      :title="contatoModal.editing ? 'Editar Contato' : 'Novo Contato'"
      centered
      @ok.prevent="saveContato"
      ok-title="Salvar"
      cancel-title="Cancelar"
      size="lg"
    >
      <b-row>
        <b-col md="8">
          <b-form-group label="Nome">
            <b-form-input v-model="contatoModal.form.clientecontatonome" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Cargo">
            <b-form-input v-model="contatoModal.form.cargo" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="Telefone">
            <b-form-input v-model="contatoModal.form.clientecontatotelefone" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Ramal">
            <b-form-input v-model="contatoModal.form.clientecontatoramal" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Celular">
            <b-form-input v-model="contatoModal.form.clientecontatocelular" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Área">
            <b-form-input v-model="contatoModal.form.clientearea" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="E-mail">
            <b-form-input v-model="contatoModal.form.clientecontatoemail" type="email" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Aniv. Dia">
            <b-form-input v-model="contatoModal.form.aniversario_dia" type="number" min="1" max="31" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Aniv. Mês">
            <b-form-input v-model="contatoModal.form.aniversario_mes" type="number" min="1" max="12" />
          </b-form-group>
        </b-col>
        <b-col md="2" class="d-flex align-items-center pt-1">
          <b-form-checkbox v-model="contatoModal.form.clientecontatoprincipal">
            Principal
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-form-group label="Observações">
        <b-form-textarea v-model="contatoModal.form.clientecontatoobservacoes" rows="2" />
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="oportunidadeModal.show"
      title="Nova Oportunidade"
      centered
      @ok.prevent="saveOportunidade"
      ok-title="Salvar"
      cancel-title="Cancelar"
    >
      <b-form-group label="Título">
        <b-form-input v-model="oportunidadeModal.form.titulo" />
      </b-form-group>
      <b-form-group label="Status">
        <b-form-select v-model="oportunidadeModal.form.status">
          <b-form-select-option value="ABERTA">Aberta</b-form-select-option>
          <b-form-select-option value="QUALIFICADA">Qualificada</b-form-select-option>
          <b-form-select-option value="EM_NEGOCIACAO">Em Negociação</b-form-select-option>
          <b-form-select-option value="GANHA">Ganha</b-form-select-option>
          <b-form-select-option value="PERDIDA">Perdida</b-form-select-option>
          <b-form-select-option value="PAUSADA">Pausada</b-form-select-option>
        </b-form-select>
      </b-form-group>
      <b-form-group label="Valor Estimado (R$)">
        <b-form-input v-model="oportunidadeModal.form.valor_estimado" type="number" step="0.01" />
      </b-form-group>
      <b-form-group label="Probabilidade (%)">
        <b-form-input v-model="oportunidadeModal.form.probabilidade" type="number" min="0" max="100" />
      </b-form-group>
      <b-form-group label="Data Prevista Fechamento">
        <b-form-input v-model="oportunidadeModal.form.data_prevista_fechamento" type="date" />
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="showPedidoModal"
      title="Detalhes do Pedido"
      centered
      size="lg"
      ok-only
      ok-title="Fechar"
    >
      <div v-if="selectedPedido" class="pedido-modal-content">
        <b-row>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Nº Pedido</span>
              <span class="pedido-detail-value">{{ selectedPedido.pedidoid }}</span>
            </div>
          </b-col>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Data</span>
              <span class="pedido-detail-value">{{ formatDate(selectedPedido.pedidodata) }}</span>
            </div>
          </b-col>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Valor Total</span>
              <span class="pedido-detail-value">{{ formatCurrency(selectedPedido.pedidovalortotal) }}</span>
            </div>
          </b-col>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Status</span>
              <b-badge :variant="getPedidoStatusVariant(selectedPedido.pedidostatusworkflow)" pill>
                {{ selectedPedido.pedidostatusworkflow || '-' }}
              </b-badge>
            </div>
          </b-col>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Sit. Sales</span>
              <b-badge :variant="getSitSalesVariant(selectedPedido.pedidositsales)" pill>
                {{ selectedPedido.pedidositsales || '-' }}
              </b-badge>
            </div>
          </b-col>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Nº Cliente</span>
              <span class="pedido-detail-value">{{ selectedPedido.pedidonumerocliente || '-' }}</span>
            </div>
          </b-col>
          <b-col sm="6">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Nota Fiscal</span>
              <span class="pedido-detail-value">{{ selectedPedido.notafiscalnumero || '-' }}</span>
            </div>
          </b-col>
          <b-col sm="6" v-if="$store.getters['preferencias/exibirDataPrevistaEntrega'] !== false">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Data Entrega</span>
              <span class="pedido-detail-value">{{ formatDate(selectedPedido.pedidodataentrega) }}</span>
            </div>
          </b-col>
          <b-col cols="12" v-if="selectedPedido.pedidoobservacoes">
            <div class="pedido-detail-item">
              <span class="pedido-detail-label">Observações</span>
              <span class="pedido-detail-value">{{ selectedPedido.pedidoobservacoes }}</span>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import crmClient from '@/api/CRM/crm.client';
import {
  BCard, BButton, BBadge, BSpinner, BRow, BCol, BFormGroup,
  BFormInput, BFormSelect, BFormSelectOption, BFormTextarea, BFormCheckbox,
  BTabs, BTab, BTable, BModal, BPagination,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CrmClienteDetail',
  components: {
    BCard, BButton, BBadge, BSpinner, BRow, BCol, BFormGroup,
    BFormInput, BFormSelect, BFormSelectOption, BFormTextarea, BFormCheckbox,
    BTabs, BTab, BTable, BModal, BPagination,
  },
  data() {
    return {
      loading: true,
      cliente: null,
      activeTab: 0,
      stats: {},
      proximasAtividades: [],
      notas: [],
      loadingNotas: false,
      notaFilter: 'todas',
      newNota: '',
      savingNota: false,
      notaAnexo: null,
      notaAnexoPreview: null,
      isDragOver: false,
      pedidos: [],
      pedidosResumo: {},
      loadingPedidos: false,
      pedidoFields: [
        { key: 'pedidoid', label: 'Nº Pedido', sortable: true },
        { key: 'pedidodata', label: 'Data', sortable: true },
        { key: 'pedidovalortotal', label: 'Valor Total', sortable: true },
        { key: 'pedidostatusworkflow', label: 'Status' },
        { key: 'pedidositsales', label: 'Sit. Sales' },
        { key: 'pedidonumerocliente', label: 'Nº Cliente' },
        { key: 'acoes', label: '' },
      ],
      selectedPedido: null,
      showPedidoModal: false,
      enderecos: [],
      editData: { crm_tipo: '', crm_observacoes: '' },
      savingDados: false,
      contatos: [],
      loadingContatos: false,
      contatoFields: [
        { key: 'clientecontatonome', label: 'Nome', sortable: true },
        { key: 'cargo', label: 'Cargo' },
        { key: 'clientecontatotelefone', label: 'Telefone' },
        { key: 'clientecontatoemail', label: 'E-mail' },
        { key: 'clientecontatocelular', label: 'Celular' },
        { key: 'aniversario', label: 'Aniversário' },
        { key: 'actions', label: 'Ações', class: 'text-center' },
      ],
      contatoModal: {
        show: false,
        editing: false,
        original: null,
        form: {
          clientecontatonome: '', cargo: '', clientecontatotelefone: '',
          clientecontatoramal: '', clientecontatocelular: '', clientecontatoemail: '',
          clientearea: '', clientecontatoprincipal: false,
          aniversario_dia: '', aniversario_mes: '',
          clientecontatoobservacoes: '',
        },
      },
      oportunidades: [],
      loadingOportunidades: false,
      oportunidadeFields: [
        { key: 'titulo', label: 'Título', sortable: true },
        { key: 'status', label: 'Status' },
        { key: 'valor_estimado', label: 'Valor Estimado' },
        { key: 'probabilidade', label: 'Prob. (%)' },
        { key: 'data_prevista_fechamento', label: 'Data Prev.' },
      ],
      oportunidadeModal: {
        show: false,
        form: { titulo: '', status: 'ABERTA', valor_estimado: '', probabilidade: '', data_prevista_fechamento: '' },
      },
    };
  },
  computed: {
    clienteId() {
      return this.$route.params.id;
    },
  },
  created() {
    this.loadCliente();
  },
  methods: {
    async loadCliente() {
      this.loading = true;
      try {
        const result = await crmClient.getCliente(this.clienteId);
        this.cliente = result.data || result;
        if (!this.cliente || !this.cliente.clienteid) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Cliente não encontrado ou sem permissão de acesso',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          });
          this.$router.replace({ name: 'crm-clientes' }).catch(() => {});
          return;
        }
        if (this.cliente.stats) {
          Object.assign(this.cliente, {
            atividades_count: this.cliente.stats.atividades_pendentes || 0,
            oportunidades_count: this.cliente.stats.oportunidades_ativas || 0,
            pedidos_count: this.cliente.stats.pedidos_total || 0,
            pics_count: this.cliente.stats.pics_total || 0,
            contatos_count: this.cliente.stats.contatos_total || 0,
            notas_count: this.cliente.stats.notas_total || 0,
          });
        }
        this.stats = {
          atividades: this.cliente.atividades_count || 0,
          oportunidades: this.cliente.oportunidades_count || 0,
          pedidos: this.cliente.pedidos_count || 0,
          pics: this.cliente.pics_count || 0,
          contatos: this.cliente.contatos_count || 0,
          notas: this.cliente.notas_count || 0,
        };
        this.editData.crm_tipo = this.cliente.crm_tipo || 'CLIENTE';
        this.editData.crm_observacoes = this.cliente.crm_observacoes || '';
        this.enderecos = this.cliente.enderecos || [];
        this.loadProximasAtividades();
        this.loadNotas();
      } catch (error) {
        console.error('Erro ao carregar cliente:', error);
        const status = error && error.response && error.response.status;
        if (status === 404 || status === 403) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Cliente não encontrado ou sem permissão de acesso',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          });
          this.$router.replace({ name: 'crm-clientes' }).catch(() => {});
          return;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar dados do cliente',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async loadProximasAtividades() {
      try {
        const result = await crmClient.listAtividades({
          cliente_id: this.clienteId,
          status: 'PENDENTE',
          limit: 5,
          sort: 'data_inicio',
          order: 'asc',
        });
        const payload = result.data || result;
        this.proximasAtividades = payload.items || payload.proximasAtividades || (Array.isArray(payload) ? payload : []);
      } catch (error) {
        console.error('Erro ao carregar atividades:', error);
      }
    },
    async loadNotas() {
      this.loadingNotas = true;
      try {
        const params = {};
        if (this.notaFilter !== 'todas') params.tipo = this.notaFilter;
        const result = await crmClient.listNotas(this.clienteId, params);
        const notasPayload = result.data || result;
        this.notas = notasPayload.items || notasPayload.rows || (Array.isArray(notasPayload) ? notasPayload : []);
        if (notasPayload.total !== undefined) {
          this.stats.notas = notasPayload.total;
        }
      } catch (error) {
        console.error('Erro ao carregar notas:', error);
      } finally {
        this.loadingNotas = false;
      }
    },
    async addNota() {
      if (!this.newNota.trim() && !this.notaAnexo) return;
      this.savingNota = true;
      try {
        await crmClient.createNota(
          this.clienteId,
          { conteudo_texto: this.newNota.trim() },
          this.notaAnexo
        );
        this.newNota = '';
        this.removeAnexo();
        await this.loadNotas();
        this.stats.notas = this.notas.length;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Nota adicionada com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (error) {
        console.error('Erro ao adicionar nota:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao adicionar nota',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.savingNota = false;
      }
    },
    onPasteFile(event) {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === 'file') {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) this.setAnexo(file);
          break;
        }
      }
    },
    onDropFile(event) {
      this.isDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.setAnexo(file);
    },
    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) this.setAnexo(file);
      event.target.value = '';
    },
    setAnexo(file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Arquivo muito grande. Máximo: 10MB',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
        return;
      }
      this.notaAnexo = file;
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => { this.notaAnexoPreview = e.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.notaAnexoPreview = null;
      }
    },
    removeAnexo() {
      this.notaAnexo = null;
      this.notaAnexoPreview = null;
    },
    getFileIcon(mimeType) {
      if (!mimeType) return 'FileIcon';
      if (mimeType.startsWith('image/')) return 'ImageIcon';
      if (mimeType === 'application/pdf') return 'FileTextIcon';
      if (mimeType.includes('word') || mimeType.includes('document')) return 'FileTextIcon';
      if (mimeType.includes('excel') || mimeType.includes('sheet') || mimeType.includes('csv')) return 'GridIcon';
      return 'FileIcon';
    },
    isImageMime(mimeType) {
      return mimeType && mimeType.startsWith('image/');
    },
    getAnexoUrl(path) {
      return `/api/uploads/crm/${path}`;
    },
    openAnexo(nota) {
      window.open(this.getAnexoUrl(nota.anexo_path), '_blank');
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },
    async saveDados() {
      this.savingDados = true;
      try {
        await crmClient.updateCliente(this.clienteId, {
          crm_tipo: this.editData.crm_tipo,
          crm_observacoes: this.editData.crm_observacoes,
        });
        this.cliente.crm_tipo = this.editData.crm_tipo;
        this.cliente.crm_observacoes = this.editData.crm_observacoes;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Dados CRM atualizados',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (error) {
        console.error('Erro ao salvar dados:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar dados',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.savingDados = false;
      }
    },
    async loadContatos() {
      this.loadingContatos = true;
      try {
        const result = await crmClient.listContatos(this.clienteId);
        this.contatos = result.data || (Array.isArray(result) ? result : []);
        this.stats.contatos = this.contatos.length;
      } catch (error) {
        console.error('Erro ao carregar contatos:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar contatos',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loadingContatos = false;
      }
    },
    openContatoModal(contato) {
      if (contato) {
        this.contatoModal.editing = true;
        this.contatoModal.original = contato;
        this.contatoModal.form = {
          clientecontatonome: contato.clientecontatonome || '',
          cargo: contato.cargo || '',
          clientecontatotelefone: contato.clientecontatotelefone || '',
          clientecontatoramal: contato.clientecontatoramal || '',
          clientecontatocelular: contato.clientecontatocelular || '',
          clientecontatoemail: contato.clientecontatoemail || '',
          clientearea: contato.clientearea || '',
          clientecontatoprincipal: !!contato.clientecontatoprincipal,
          aniversario_dia: contato.aniversario_dia || '',
          aniversario_mes: contato.aniversario_mes || '',
          clientecontatoobservacoes: contato.clientecontatoobservacoes || '',
        };
      } else {
        this.contatoModal.editing = false;
        this.contatoModal.original = null;
        this.contatoModal.form = {
          clientecontatonome: '', cargo: '', clientecontatotelefone: '',
          clientecontatoramal: '', clientecontatocelular: '', clientecontatoemail: '',
          clientearea: '', clientecontatoprincipal: false,
          aniversario_dia: '', aniversario_mes: '',
          clientecontatoobservacoes: '',
        };
      }
      this.contatoModal.show = true;
    },
    async saveContato() {
      try {
        if (this.contatoModal.editing && this.contatoModal.original) {
          const orig = this.contatoModal.original;
          await crmClient.updateContato(
            this.clienteId,
            orig.clientecontatoseq,
            orig.clienteiderp || '',
            this.contatoModal.form,
          );
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Contato atualizado',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          });
        } else {
          await crmClient.createContato(this.clienteId, this.contatoModal.form);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Contato criado',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          });
        }
        this.contatoModal.show = false;
        this.loadContatos();
      } catch (error) {
        console.error('Erro ao salvar contato:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar contato',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async deleteContato(contato) {
      if (!confirm('Deseja remover este contato?')) return;
      try {
        await crmClient.deleteContato(
          this.clienteId,
          contato.clientecontatoseq,
          contato.clienteiderp || '',
        );
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Contato removido',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.loadContatos();
      } catch (error) {
        console.error('Erro ao remover contato:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao remover contato',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async loadOportunidades() {
      this.loadingOportunidades = true;
      try {
        const result = await crmClient.listOportunidades({ cliente_id: this.clienteId });
        const oppPayload = result.data || result;
        this.oportunidades = oppPayload.items || oppPayload.rows || (Array.isArray(oppPayload) ? oppPayload : []);
        this.stats.oportunidades = this.oportunidades.length;
      } catch (error) {
        console.error('Erro ao carregar oportunidades:', error);
      } finally {
        this.loadingOportunidades = false;
      }
    },
    openOportunidadeModal() {
      this.oportunidadeModal.form = {
        titulo: '', status: 'ABERTA', valor_estimado: '', probabilidade: '', data_prevista_fechamento: '',
      };
      this.oportunidadeModal.show = true;
    },
    async saveOportunidade() {
      try {
        await crmClient.createOportunidade({
          ...this.oportunidadeModal.form,
          cliente_id: this.clienteId,
        });
        this.oportunidadeModal.show = false;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Oportunidade criada',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.loadOportunidades();
      } catch (error) {
        console.error('Erro ao criar oportunidade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao criar oportunidade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async onConcluirAtividade(atv) {
      if (!confirm('Deseja concluir esta atividade?')) return;
      try {
        await crmClient.concluirAtividade(atv.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atividade concluída',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.loadProximasAtividades();
      } catch (error) {
        console.error('Erro ao concluir atividade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao concluir atividade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    onTabChange(tabIndex) {
      if (tabIndex === 0) this.loadNotas();
      if (tabIndex === 2) this.loadContatos();
      if (tabIndex === 3) this.loadOportunidades();
      if (tabIndex === 4) this.loadPedidos();
    },
    isManualNota(nota) {
      return nota.tipo && nota.tipo.toUpperCase() === 'MANUAL';
    },
    getTipoBadgeVariant(tipo) {
      const map = { CLIENTE: 'light-primary', LEAD: 'light-warning', PROSPECT: 'light-info', DESQUALIFICADO: 'light-secondary' };
      return map[tipo] || 'light-secondary';
    },
    getOportStatusVariant(status) {
      const map = { ABERTA: 'light-info', QUALIFICADA: 'light-primary', EM_NEGOCIACAO: 'light-warning', GANHA: 'light-success', PERDIDA: 'light-danger', PAUSADA: 'light-secondary' };
      return map[status] || 'light-secondary';
    },
    formatCnpj(cnpj) {
      if (!cnpj) return '-';
      const c = cnpj.replace(/\D/g, '');
      if (c.length === 14) return c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      return cnpj;
    },
    async loadPedidos() {
      this.loadingPedidos = true;
      try {
        const result = await crmClient.listPedidos(this.clienteId);
        const data = result.data || result;
        this.pedidos = data.pedidos || [];
        this.pedidosResumo = data.resumo || {};
      } catch (error) {
        console.error('Erro ao carregar pedidos:', error);
        this.pedidos = [];
        this.pedidosResumo = {};
      } finally {
        this.loadingPedidos = false;
      }
    },
    onPedidoClick(item) {
      const canAccess = this.$ability.can('read', 'PedidosVenda');
      if (canAccess) {
        this.$router.push({
          name: 'pedidos-vendas-detalhes',
          params: { pedidoid: item.pedidoid, clienteid: this.clienteId },
        });
      } else {
        this.selectedPedido = item;
        this.showPedidoModal = true;
      }
    },
    getPedidoStatusVariant(status) {
      if (!status) return 'secondary';
      const s = status.toUpperCase();
      if (s.includes('APROVADO') || s.includes('FATURADO')) return 'success';
      if (s.includes('PENDENTE') || s.includes('ANALISE')) return 'warning';
      if (s.includes('CANCELADO') || s.includes('REJEITADO')) return 'danger';
      return 'info';
    },
    getSitSalesVariant(sit) {
      if (!sit) return 'secondary';
      const s = sit.toUpperCase();
      if (s.includes('OK') || s.includes('INTEGRADO')) return 'success';
      if (s.includes('ERRO') || s.includes('FALHA')) return 'danger';
      if (s.includes('PENDENTE')) return 'warning';
      return 'info';
    },
    formatDate(val) {
      if (!val) return '-';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('pt-BR');
    },
    formatDateTime(val) {
      if (!val) return '-';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    formatCurrency(value) {
      return 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.crm-detail-page {
  padding: 0.75rem;
  position: relative;
}
.page-header {
  margin-bottom: 1rem;
}
.detail-layout {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
}
.detail-sidebar {
  width: 280px;
  min-width: 280px;
  flex-shrink: 0;
}
.detail-main {
  flex: 1;
  min-width: 0;
}
.sidebar-card {
  margin-bottom: 1rem;
}
.client-summary {
  padding: 1.25rem;
  text-align: center;
}
.client-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.client-fantasia {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}
.client-cnpj {
  font-size: 0.85rem;
  font-family: 'SFMono-Regular', Consolas, monospace;
  margin-bottom: 0.75rem;
}
.client-badges {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}
.stats-section,
.activities-section {
  padding: 1rem 1.25rem;
}
.stats-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
  font-weight: 600;
}
.stat-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  cursor: pointer;
}
.stat-label {
  flex: 1;
  font-size: 0.9rem;
}
.stat-count {
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.1rem 0.5rem;
  border-radius: 8px;
  min-width: 28px;
  text-align: center;
}
.activity-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
}
.activity-title {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
}
.activity-date {
  display: block;
  font-size: 0.75rem;
}
.tab-content-inner {
  padding: 0.5rem 0;
}
.tab-toolbar {
  margin-bottom: 1rem;
}
.timeline-filters {
  margin-bottom: 1rem;
}
.add-note-form {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  transition: background-color 0.2s ease;
}
.add-note-form.drag-over {
  border-radius: 0.5rem;
  border: 2px dashed var(--primary);
  padding: 0.5rem;
}
.note-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}
.note-actions-left {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.anexo-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
}
.anexo-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.anexo-name {
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.anexo-size {
  font-size: 0.75rem;
  margin-left: 0.25rem;
  flex-shrink: 0;
}
.anexo-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 0.25rem;
}
.note-item {
  padding: 0.75rem 0;
}
.note-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}
.note-date {
  font-size: 0.8rem;
}
.note-author {
  font-size: 0.8rem;
  margin-bottom: 0.35rem;
}
.note-text {
  font-size: 0.9rem;
  margin-bottom: 0;
  white-space: pre-wrap;
}
.note-attachment {
  margin-top: 0.5rem;
}
.attachment-image {
  max-width: 300px;
  max-height: 200px;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: opacity 0.2s;
}
.attachment-image:hover {
  opacity: 0.85;
}
.attachment-file {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  border-radius: 0.375rem;
  text-decoration: none;
  font-size: 0.85rem;
}
.attachment-file:hover {
  text-decoration: none;
}
.attachment-size {
  font-size: 0.75rem;
  margin-left: 0.35rem;
}
.no-items {
  text-align: center;
  font-size: 0.9rem;
  padding: 1rem 0;
}
.pedidos-resumo {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
}
.pedido-modal-content {
  padding: 0.5rem 0;
}
.pedido-detail-item {
  margin-bottom: 1rem;
}
.pedido-detail-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.2rem;
}
.pedido-detail-value {
  font-size: 0.95rem;
  font-weight: 500;
}
.dados-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
}
.dados-section-title {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary);
}
.dados-field {
  margin-bottom: 1rem;
}
.dados-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.2rem;
}
.dados-value {
  font-size: 0.9rem;
  font-weight: 500;
  word-break: break-word;
}
.dados-value-block {
  display: block;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.25rem;
  font-weight: 400;
  white-space: pre-wrap;
  font-size: 0.85rem;
}
.endereco-card {
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
}
.endereco-header {
  margin-bottom: 0.75rem;
}
.endereco-obs {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  font-style: italic;
}
.dados-section-crm {
  border-radius: 8px;
  padding: 1.25rem;
}
@media (max-width: 992px) {
  .detail-layout {
    flex-direction: column;
  }
  .detail-sidebar {
    width: 100%;
    min-width: auto;
  }
}
</style>
